import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from "recharts";
import { computeDashboardStats } from "../../utils/stats";

const STAT_CONFIG = [
  { key: "totalParticipants", suffix: "", accent: "teal", icon: "👥" },
  { key: "totalAttempts", suffix: "", accent: "blue", icon: "📋" },
  { key: "averageScore", suffix: "%", accent: "green", icon: "📈" },
  { key: "highestScore", suffix: "%", accent: "purple", icon: "🏆" },
  { key: "lowestScore", suffix: "%", accent: "orange", icon: "📉" },
];

const StatCard = ({ label, value, suffix, accent, icon }) => (
  <div className={`admin-stat-card admin-stat-${accent}`}>
    <div className="admin-stat-top">
      <span className="admin-stat-icon">{icon}</span>
    </div>
    <p className="admin-stat-value">{value}{suffix}</p>
    <p className="admin-stat-label">{label}</p>
  </div>
);

const AdminDashboard = ({ results, t }) => {
  const stats = computeDashboardStats(results);

  if (!results.length) {
    return (
      <div className="admin-empty">
        <div className="admin-empty-icon">📊</div>
        <h3>{t.noResultsYet}</h3>
        <p>{t.dashboardEmptyHint}</p>
      </div>
    );
  }

  const labels = {
    totalParticipants: t.totalParticipants,
    totalAttempts: t.totalAttempts,
    averageScore: t.averageScore,
    highestScore: t.highestScore,
    lowestScore: t.lowestScore,
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-stats-row">
        {STAT_CONFIG.map(({ key, suffix, accent, icon }) => (
          <StatCard
            key={key}
            label={labels[key]}
            value={stats[key]}
            suffix={suffix}
            accent={accent}
            icon={icon}
          />
        ))}
      </div>

      <div className="admin-charts-row">
        <div className="admin-chart-panel">
          <div className="admin-panel-header">
            <h3>{t.scoreDistribution}</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.scoreDistribution} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="range" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
              <Bar dataKey="count" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" />
                  <stop offset="100%" stopColor="#0f766e" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-chart-panel">
          <div className="admin-panel-header">
            <h3>{t.attemptsOverTime}</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats.attemptsByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#0284c7"
                strokeWidth={2}
                fill="url(#areaGradient)"
              />
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#bae6fd" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#bae6fd" stopOpacity={0.1} />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
